import React from 'react';
import { connect, ConnectedProps } from '@cerebral/react';
import { state, sequences, moduleSequences } from 'app/pwa/app.cerebral';
import { Wrapper, CloseIconContainer, CloseIcon } from './elements';
import ActMod13 from 'dc/ActMod13';
import Subhead01 from 'dc/Subhead01';
import Lab400 from 'dc/Lab400';
import Indicator from 'challenge/common/ActivityModal/Indicator';
import {
  useText,
  useFormatIndicator,
  useGetActivityIconUrl,
} from 'common/hooks';
import { sortIndicatorsForChallenge } from 'utils/challenge';

const deps = {
  isHabit:
    state.challenge.userChallenge.habits[
      state.challenge.activityModal.activityKey
    ],
  activity:
    state.appContainer.activities[state.challenge.activityModal.activityKey],
  activityKey: state.challenge.activityModal.activityKey,
  campaignIndicator: state.challenge.campaign.indicator,
  closeActivityModalClicked: sequences`challenge.activityModal.closeActivityModalClicked`,
  toggleHabitClicked: sequences`challenge.log.toggleHabitClicked`,
  showTagModal: moduleSequences.challenge.tagModal.showTagModalClicked,
};

export function ActivityModalContent({
  isHabit,
  activity,
  activityKey,
  campaignIndicator,
  closeActivityModalClicked,
  toggleHabitClicked,
  showTagModal,
}: typeof deps & ConnectedProps): React.ReactElement {
  const t = useText();
  const formatIndicator = useFormatIndicator();
  const getActivityIconUrl = useGetActivityIconUrl();

  if (!activity) return null;
  const indicators = sortIndicatorsForChallenge(
    activity,
    campaignIndicator
  ).map(indicatorObject => ({
    ...indicatorObject,
    value: formatIndicator(indicatorObject.value, indicatorObject.key),
  }));

  return (
    <Wrapper>
      <CloseIconContainer>
        <CloseIcon
          onClick={() => closeActivityModalClicked()}
          icon={'icon-close'}
        />
      </CloseIconContainer>
      <ActMod13
        onHabitClick={
          activityKey && (() => toggleHabitClicked({ activityKey }))
        }
        activityIcon={getActivityIconUrl(activity?.icon)}
        activityIconAlt={''}
        name={activity.title}
        description={activity.description}
        habitText={t(`challenge.SeH002.${isHabit ? 'remove' : 'add'}_habit`)}
        headerText={t(`challenge.ActMod02.documented_benefits`)}
      />
      {indicators.map(indicator => (
        <Indicator key={indicator.key} indicator={indicator.key} />
      ))}
      <Subhead01 text={t(`challenge.ActMod02.topic_tags`)} />
      {Object.values(activity.tags || {})
        .flat()
        .filter(tag => t.has(`challenge.tags.${tag}.name`))
        .map(tag => (
          <Lab400
            key={`${activityKey} + ${tag}`}
            icon={'icon-keyboard_arrow_right'}
            onClick={() =>
              showTagModal({
                tag: tag,
                activityModalKey: activityKey,
              })
            }
          >
            {t(`challenge.tags.${tag}.name`)}
          </Lab400>
        ))}
    </Wrapper>
  );
}

export default connect(deps, ActivityModalContent);
